import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(16px); }
`;

const dotPulse = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40%           { transform: scale(1);   opacity: 1; }
`;

const springBounce = keyframes`
  0%   { transform: scale(1); }
  20%  { transform: scale(1.5); }
  42%  { transform: scale(0.82); }
  65%  { transform: scale(1.18); }
  80%  { transform: scale(0.96); }
  100% { transform: scale(1); }
`;

const particleFly = keyframes`
  0%   { transform: rotate(var(--angle)) translateY(0)    scale(1);   opacity: 1; }
  100% { transform: rotate(var(--angle)) translateY(18px) scale(0);   opacity: 0; }
`;

const badgePulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(250,82,82,0.4); }
  50%      { transform: scale(1.15); box-shadow: 0 0 0 4px rgba(250,82,82,0); }
`;

export const PanelWrapper = styled.div<{ isClosing?: boolean }>(({ theme, isClosing }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: theme.radius.md,
  overflow: 'hidden',
  boxShadow: theme.shadows.lg,
  backgroundColor: 'white',
  border: `1px solid ${theme.colors.gray[2]}`,
  animation: isClosing
    ? `${slideDown} 0.18s ease-in forwards`
    : `${slideUp} 0.2s ease-out`,
}));

export const ResizeHandle = styled.div(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: 20,
  height: 20,
  cursor: 'nw-resize',
  zIndex: 10,
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 5,
    left: 5,
    width: 9,
    height: 9,
    borderTop: `2px solid ${theme.colors.gray[4]}`,
    borderLeft: `2px solid ${theme.colors.gray[4]}`,
    borderRadius: '2px 0 0 0',
    transition: 'border-color 0.15s ease',
  },
  '&:hover::before': {
    borderColor: theme.colors.blue[5],
  },
}));

export const MessageList = styled.div({
  flex: 1,
  overflowY: 'auto',
  padding: '0.75rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
});

export const BubbleWrapper = styled.div<{ align: 'left' | 'right' }>(({ align }) => ({
  position: 'relative',
  alignSelf: align === 'right' ? 'flex-end' : 'flex-start',
  maxWidth: '80%',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  '&:hover .bubble-actions': { opacity: 1 },
}));

export const UserBubble = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.blue[6],
  color: 'white',
  borderRadius: theme.radius.md,
  padding: '0.4rem 0.75rem',
  fontSize: theme.fontSizes.sm,
  wordBreak: 'break-word',
  animation: `${fadeSlideIn} 0.18s ease-out`,
}));

export const AssistantBubble = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.gray[1],
  color: theme.black,
  borderRadius: theme.radius.md,
  padding: '0.4rem 0.75rem',
  fontSize: theme.fontSizes.sm,
  wordBreak: 'break-word',
  animation: `${fadeSlideIn} 0.18s ease-out`,
  transition: 'background-color 0.15s ease',
  whiteSpace: 'pre-wrap',
  '&:hover': { backgroundColor: theme.colors.gray[2] },
}));

export const BubbleActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 3,
  opacity: 0,
  transition: 'opacity 0.15s ease',
});

export const CopyButton = styled.button<{ copied?: boolean }>(({ theme, copied }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 7px',
  borderRadius: theme.radius.sm,
  border: `1px solid ${theme.colors.gray[3]}`,
  backgroundColor: copied ? theme.colors.green[0] : 'white',
  color: copied ? theme.colors.green[7] : theme.colors.gray[6],
  fontSize: 11,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  '&:hover': {
    backgroundColor: theme.colors.gray[0],
    borderColor: theme.colors.gray[4],
    color: theme.black,
  },
}));

export const Timestamp = styled.span(({ theme }) => ({
  fontSize: 11,
  color: theme.colors.gray[5],
  padding: '0 2px',
  opacity: 0.6,
  userSelect: 'none',
}));

export const InputRow = styled.div(({ theme }) => ({
  display: 'flex',
  gap: '0.5rem',
  padding: '0.5rem',
  borderTop: `1px solid ${theme.colors.gray[2]}`,
  flexShrink: 0,
}));

export const TypingIndicator = styled.div({
  alignSelf: 'flex-start',
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '0.5rem 0.75rem',
  animation: `${fadeSlideIn} 0.18s ease-out`,
});

export const TypingDot = styled.span<{ delay: number }>(({ delay }) => ({
  width: 7,
  height: 7,
  borderRadius: '50%',
  backgroundColor: '#adb5bd',
  display: 'inline-block',
  animation: `${dotPulse} 1.2s ease-in-out ${delay}s infinite`,
}));

export const FABWrapper = styled.div({
  position: 'relative',
  display: 'inline-flex',
});

export const FAB = styled.button(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: theme.colors.blue[6],
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    transform: 'scale(1.08)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.22)',
  },
  '&:active': { transform: 'scale(0.95)' },
}));

export const UnreadBadge = styled.span(({ theme }) => ({
  position: 'absolute',
  top: -2,
  right: -2,
  minWidth: 18,
  height: 18,
  borderRadius: 9,
  backgroundColor: theme.colors.red[6],
  color: 'white',
  fontSize: 10,
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 4px',
  border: '2px solid white',
  animation: `${badgePulse} 1.8s ease-in-out infinite`,
}));

export const SendButton = styled.button<{ disabled: boolean }>(({ theme, disabled }) => ({
  width: 30,
  height: 30,
  borderRadius: theme.radius.sm,
  backgroundColor: disabled ? theme.colors.gray[3] : theme.colors.blue[6],
  color: 'white',
  border: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'background-color 0.15s ease, transform 0.1s ease',
  '&:hover:not([disabled])': { backgroundColor: theme.colors.blue[7] },
  '&:active:not([disabled])': { transform: 'scale(0.9)' },
}));

export const WelcomeScreen = styled.div({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1rem',
  gap: '0.75rem',
});

export const WelcomeTitle = styled.p(({ theme }) => ({
  fontSize: theme.fontSizes.sm,
  fontWeight: 600,
  color: theme.colors.gray[7],
  margin: 0,
  textAlign: 'center',
}));

export const OptionGrid = styled.div({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '0.5rem',
  width: '100%',
});

export const OptionCard = styled.button(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 5,
  padding: '0.6rem 0.75rem',
  borderRadius: theme.radius.md,
  border: `1px solid ${theme.colors.gray[2]}`,
  backgroundColor: theme.colors.gray[0],
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: theme.colors.blue[0],
    borderColor: theme.colors.blue[3],
    transform: 'translateY(-1px)',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
  },
  '&:active': { transform: 'translateY(0)' },
}));

export const OptionCardTitle = styled.span(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  fontWeight: 600,
  color: theme.colors.gray[8],
}));

export const OptionCardDesc = styled.span(({ theme }) => ({
  fontSize: 10,
  color: theme.colors.gray[5],
  lineHeight: 1.3,
}));

export const HistoryList = styled.div({
  flex: 1,
  overflowY: 'auto',
  padding: '0.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.4rem',
});

export const HistoryItem = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  padding: '0.5rem 0.65rem',
  borderRadius: theme.radius.sm,
  border: `1px solid ${theme.colors.gray[2]}`,
  backgroundColor: theme.colors.gray[0],
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: theme.colors.blue[0],
    borderColor: theme.colors.blue[2],
  },
}));

export const HistoryItemTitle = styled.span(({ theme }) => ({
  fontSize: theme.fontSizes.xs,
  fontWeight: 500,
  color: theme.colors.gray[8],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}));

export const HistoryItemMeta = styled.span(({ theme }) => ({
  fontSize: 10,
  color: theme.colors.gray[5],
}));

export const HistoryItemBadge = styled.span<{ intent: string }>(({ theme, intent }) => {
  const colors: Record<string, { bg: string; text: string }> = {
    'documentation':     { bg: theme.colors.blue[0],   text: theme.colors.blue[7] },
    'feature-request':   { bg: theme.colors.yellow[0], text: theme.colors.yellow[8] },
    'bug-report':        { bg: theme.colors.red[0],    text: theme.colors.red[7] },
    'mapping-assistant': { bg: theme.colors.green[0],  text: theme.colors.green[7] },
  };
  const c = colors[intent] ?? { bg: theme.colors.gray[1], text: theme.colors.gray[6] };
  return {
    fontSize: 9,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
    padding: '1px 5px',
    borderRadius: theme.radius.sm,
    backgroundColor: c.bg,
    color: c.text,
  };
});

export const HistoryItemActions = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 4,
  marginTop: 2,
});

export const HistoryEmpty = styled.div(({ theme }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: theme.fontSizes.xs,
  color: theme.colors.gray[5],
  padding: '1.5rem 1rem',
  textAlign: 'center',
}));

export const ErrorBubble = styled.div(({ theme }) => ({
  backgroundColor: theme.colors.red[0],
  color: theme.colors.red[7],
  borderRadius: theme.radius.md,
  padding: '0.4rem 0.75rem',
  fontSize: theme.fontSizes.sm,
  border: `1px solid ${theme.colors.red[2]}`,
  animation: `${fadeSlideIn} 0.18s ease-out`,
}));

export const FeedbackButton = styled.button<{ active?: boolean; positive?: boolean; springing?: boolean }>(
  ({ theme, active, positive, springing }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '2px 5px',
    borderRadius: theme.radius.sm,
    border: `1px solid ${
      active ? (positive ? theme.colors.green[4] : theme.colors.red[4]) : theme.colors.gray[3]
    }`,
    backgroundColor: active ? (positive ? theme.colors.green[0] : theme.colors.red[0]) : 'white',
    color: active
      ? positive ? theme.colors.green[7] : theme.colors.red[6]
      : theme.colors.gray[5],
    fontSize: 12,
    cursor: 'pointer',
    transition: 'border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease',
    animation: springing ? `${springBounce} 0.45s cubic-bezier(0.36, 0.07, 0.19, 0.97)` : 'none',
    '&:hover': {
      backgroundColor: positive ? theme.colors.green[0] : theme.colors.red[0],
      color: positive ? theme.colors.green[7] : theme.colors.red[6],
      borderColor: positive ? theme.colors.green[4] : theme.colors.red[4],
    },
  })
);

export const ParticleWrap = styled.div({
  position: 'relative',
  display: 'inline-flex',
  alignItems: 'center',
});

export const ParticleOrigin = styled.div({
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: 0,
  height: 0,
  pointerEvents: 'none',
  zIndex: 20,
});

export const Particle = styled.span<{ positive: boolean }>(({ theme, positive }) => ({
  position: 'absolute',
  width: 5,
  height: 5,
  marginLeft: -2.5,
  marginTop: -2.5,
  borderRadius: '50%',
  backgroundColor: positive ? theme.colors.green[5] : theme.colors.red[5],
  animation: `${particleFly} 0.5s ease-out forwards`,
}));

export const ScrollToBottomBtn = styled.button<{ visible: boolean }>(({ theme, visible }) => ({
  position: 'absolute',
  bottom: 56,
  right: 10,
  width: 26,
  height: 26,
  borderRadius: '50%',
  backgroundColor: theme.colors.blue[6],
  color: 'white',
  border: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
  opacity: visible ? 1 : 0,
  transform: visible ? 'translateY(0)' : 'translateY(8px)',
  transition: 'opacity 0.2s ease, transform 0.2s ease',
  pointerEvents: visible ? 'auto' : 'none',
}));
